"use client";

import { useQuery } from "@tanstack/react-query";
import { SCRATCH_TEMPLATE_ID } from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/care-plan/new/[templateId]/page";
import { DynamicNameKey, SiteMapItem, extractRouteParams, getRelativePath } from "@/config/sitemap";
import { isNoteId } from "@/hooks/use-selected-note";
import { formatDate, formatName } from "@/lib/format-data";
import { carePlanQueryOptions } from "@/api/provider/patient/care-plan-query";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { carePlanTemplateQueryOptions } from "@/api/provider/settings/care-plan-template-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";

/**
 * Hook that enhances sitemap path items with dynamic names based on route params.
 * Currently supports patient names for patient detail pages.
 */
export function useEnhancedSitemapPath(route: string): SiteMapItem[] {
  const basePath = getRelativePath(route);

  // Find items that need dynamic name resolution
  // Check for care plan template first (settings route, no patient ID)
  // Use findLast to get the most specific match (in case there are nested routes)
  const carePlanTemplateItem = [...basePath].reverse().find(item => item.dynamicNameKey === DynamicNameKey.CARE_PLAN_TEMPLATE && item.route);

  // Note editor: the crumb carries the note's patient/title/visit-date so the
  // chart context stays pinned at the top of the screen while the note scrolls.
  const noteItem = [...basePath].reverse().find(item_0 => item_0.dynamicNameKey === DynamicNameKey.NOTE && item_0.route);

  // Find the most specific patient-related item (last match = most nested)
  // Use findLast so we get Care Plans (/patient/:id/care-plan/:carePlanId) instead of
  // Patient Chart (/patient/:id) when on a care plan page — otherwise we'd miss carePlanId
  const patientItem = [...basePath].reverse().find(item_1 => (item_1.dynamicNameKey === DynamicNameKey.PATIENT || item_1.dynamicNameKey === DynamicNameKey.CARE_PLAN) && item_1.route);

  // Extract IDs from routes
  let patientId: string | undefined;
  let carePlanId: string | undefined;
  let carePlanTemplateId: string | undefined;
  let noteId: string | undefined;
  if (noteItem?.route) {
    const params = extractRouteParams(noteItem.route, route);
    noteId = params.id;
  }
  if (carePlanTemplateItem?.route) {
    const params_0 = extractRouteParams(carePlanTemplateItem.route, route);
    carePlanTemplateId = params_0.templateId;
  }
  if (patientItem?.route) {
    const params_1 = extractRouteParams(patientItem.route, route);
    patientId = params_1.id;
    carePlanId = params_1.carePlanId;
  }

  // Fetch patient or care plan data if needed
  const {
    data: patient,
    isPending: isPatientLoading
  } = useQuery({
    ...patientQueryOptions.detail(patientId || ""),
    enabled: !!patientId
  });
  const {
    data: carePlan,
    isPending: isCarePlanLoading
  } = useQuery({
    ...carePlanQueryOptions.detail(carePlanId || ""),
    enabled: !!carePlanId && carePlanId !== SCRATCH_TEMPLATE_ID
  });
  const {
    data: carePlanTemplate,
    isPending: isCarePlanTemplateLoading
  } = useQuery({
    ...carePlanTemplateQueryOptions.detail(carePlanTemplateId || ""),
    enabled: !!carePlanTemplateId && carePlanTemplateId !== SCRATCH_TEMPLATE_ID
  });
  const {
    data: note,
    isPending: isNoteLoading
  } = useQuery({
    ...noteQueryOptions.detail(noteId || ""),
    enabled: !!noteId && isNoteId(noteId)
  });

  // Enhance items with dynamic names
  const enhanced = basePath.flatMap(item_2 => {
    if (item_2.dynamicNameKey === DynamicNameKey.PATIENT && patient && !isPatientLoading) {
      return {
        ...item_2,
        name: formatName(patient.firstName, patient.lastName)
      };
    }
    if (item_2.dynamicNameKey === DynamicNameKey.CARE_PLAN && carePlan && !isCarePlanLoading) {
      return {
        ...item_2,
        name: carePlan.name
      };
    }
    if (item_2.dynamicNameKey === DynamicNameKey.CARE_PLAN_TEMPLATE && carePlanTemplate && !isCarePlanTemplateLoading) {
      return {
        ...item_2,
        name: carePlanTemplate.name
      };
    }
    if (item_2.dynamicNameKey === DynamicNameKey.NOTE && note && !isNoteLoading) {
      const patientName = formatName(note.patient?.firstName, note.patient?.lastName) || "Unknown patient";
      const rawTitle = note.title || note.template?.name || "Note";
      // note.title is generated as "<patient> - <service>", so it already leads
      // with the patient name; strip that prefix so the crumb doesn't print the
      // patient twice ("Test Test - Test Test - ...").
      const prefix = `${patientName} - `;
      const noteTitle = rawTitle.startsWith(prefix) ? rawTitle.slice(prefix.length) : rawTitle;
      const visit = note.visitDate ?? note.createdDate;
      const patient_0 = note.patient;
      // Patient and note become two crumbs so the provider reads whose chart
      // they're in without the name repeating. The patient crumb links to the
      // chart, like every other crumb navigates.
      return [{
        ...item_2,
        name: patientName,
        route: patient_0 ? `/patient/${patient_0.id}` : undefined,
        breadcrumbMeta: undefined
      }, {
        ...item_2,
        name: noteTitle,
        breadcrumbMeta: visit ? formatDate(visit) : undefined
      }];
    }
    return item_2;
  });
  return enhanced;
}